<template>
  <transition name="fade-scale">
    <div v-if="visible" class="loading-screen-overlay">
      <div class="loading-content" :class="{ 'fade-out-elements': isFadingOut }">
        <div class="loading-image-container">
          <img
            src="/images/logo-main.png"
            alt="Suntop x One Piece"
            class="loading-image"
            :class="{ 'pulse': isLoading && imageLoaded }"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            @load="onImageLoad"
          >
          <div class="loading-progress-container">
            <div class="loading-progress-bar">
              <div 
                class="loading-progress-fill"
                :style="{ width: progress + '%' }"
              ></div>
            </div>
            <div class="loading-percentage">{{ Math.round(progress) }}%</div>
          </div>
        </div>
        
        <div class="loading-tips">
          <transition name="slide-fade" mode="out-in">
            <p :key="currentTip" class="loading-tip">{{ currentTip }}</p>
          </transition>
        </div>
        
        <div class="loading-spinner">
          <div class="spinner-circle"></div>
          <div class="spinner-circle"></div>
          <div class="spinner-circle"></div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  visible: {
    type: Boolean,
    default: true
  },
  duration: {
    type: Number,
    default: 1000
  }
})

const emit = defineEmits(['complete'])

const { t } = useI18n()
const progress = ref(0)
const isLoading = ref(true)
const isFadingOut = ref(false)
const currentTipIndex = ref(0)
const imageLoaded = ref(false)

const tips = computed(() => [
  t('loading_tips.tip1', 'Préparez-vous à faire tourner la roue et gagner des prix incroyables!'),
  t('loading_tips.tip2', 'iPad Mini, sacs d\'école et plus vous attendent!'),
  t('loading_tips.tip3', 'Scannez votre pack Suntop pour participer!'),
  t('loading_tips.tip4', 'Montres, ballons de football et sacs repas à gagner!'),
  t('loading_tips.tip5', 'Complétez votre inscription pour tenter votre chance!')
])

const currentTip = computed(() => tips.value[currentTipIndex.value])

let progressInterval = null
let tipInterval = null

const onImageLoad = () => {
  imageLoaded.value = true
}

onMounted(() => {
  // Remove static loading screen (seamless takeover by Vue)
  const staticLoader = document.getElementById('static-loading')
  if (staticLoader) {
    staticLoader.remove()
  }

  // Image should already be preloaded via HTML link tags
  // Start animation immediately for faster perceived performance

  // Animate progress bar
  const startTime = Date.now()
  const animateProgress = () => {
    const elapsed = Date.now() - startTime
    const newProgress = Math.min((elapsed / props.duration) * 100, 100)
    progress.value = newProgress

    if (newProgress >= 100) {
      isLoading.value = false

      // Brief pause to show completion (feels intentional, not stuck)
      setTimeout(() => {
        // Start smooth fade out
        isFadingOut.value = true

        // Wait for fade out animation, then emit complete
        setTimeout(() => {
          emit('complete')
        }, 400) // Match fade-out duration
      }, 150) // Brief completion pause
    } else {
      requestAnimationFrame(animateProgress)
    }
  }
  requestAnimationFrame(animateProgress)

  // Rotate tips
  tipInterval = setInterval(() => {
    currentTipIndex.value = (currentTipIndex.value + 1) % tips.value.length
  }, 2000)
})

onUnmounted(() => {
  if (tipInterval) clearInterval(tipInterval)
})
</script>

<style lang="scss" scoped>
.loading-screen-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #1a1a2e 0%, #0f0f1e 100%);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  // Performance optimizations
  will-change: opacity;
  contain: strict;
  
  &::before {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle at 50% 50%, rgba(255, 204, 0, 0.1) 0%, transparent 70%);
    // Disable heavy animation for faster initial render
    // animation: rotate 20s linear infinite;
    
    // GPU acceleration
    will-change: transform;
    transform: translateZ(0);
  }
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.loading-content {
  position: relative;
  text-align: center;
  z-index: 1;
}

.loading-image-container {
  position: relative;
  margin-bottom: 2rem;
}

.loading-image {
  width: 300px;
  max-width: 80vw;
  height: auto;
  // Remove heavy filter effects for faster initial render
  // filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.5));
  transition: transform 0.3s ease;
  
  // GPU acceleration optimizations
  will-change: transform;
  transform: translateZ(0);
  contain: layout style paint;
  
  &.pulse {
    // Reduced animation intensity and duration for faster render
    animation: pulse 1.5s ease-in-out infinite;
  }
}

@keyframes pulse {
  0%, 100% { 
    transform: translateZ(0) scale(1); 
  }
  50% { 
    transform: translateZ(0) scale(1.02); // Reduced scale for less render work
  }
}

.loading-progress-container {
  margin-top: 2rem;
  position: relative;
}

.loading-progress-bar {
  width: 300px;
  max-width: 80vw;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
  margin: 0 auto;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.loading-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #ffcc00 0%, #ff6b00 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
  box-shadow: 0 0 10px rgba(255, 204, 0, 0.5);
}

.loading-percentage {
  margin-top: 0.5rem;
  font-size: 1.2rem;
  font-weight: bold;
  color: #ffcc00;
  text-shadow: 0 0 10px rgba(255, 204, 0, 0.5);
}

.loading-tips {
  margin: 2rem 0;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-tip {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  max-width: 300px;
  line-height: 1.4;
  margin: 0;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
}

.spinner-circle {
  width: 12px;
  height: 12px;
  background: #ffcc00;
  border-radius: 50%;
  animation: bounce 1.4s ease-in-out infinite both;
  
  &:nth-child(1) { animation-delay: -0.32s; }
  &:nth-child(2) { animation-delay: -0.16s; }
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

// Transitions
.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: all 0.5s ease;
}

.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from {
  transform: translateY(10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

// Unified smooth fade-out animation for all elements
.fade-out-elements {
  animation: unifiedFadeOut 0.4s cubic-bezier(0.4, 0, 0.6, 1) forwards;

  // Add completion flourish to progress bar
  .loading-progress-fill {
    animation: completionPulse 0.3s ease-out forwards;
  }
}

@keyframes unifiedFadeOut {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0.95);
  }
}

@keyframes completionPulse {
  0% {
    box-shadow: 0 0 10px rgba(255, 204, 0, 0.5);
  }
  50% {
    box-shadow: 0 0 25px rgba(255, 204, 0, 0.9), 0 0 40px rgba(255, 204, 0, 0.6);
  }
  100% {
    box-shadow: 0 0 10px rgba(255, 204, 0, 0.5);
  }
}

// Responsive
@media (max-width: 768px) {
  .loading-image {
    width: 200px;
  }
  
  .loading-progress-bar {
    width: 200px;
  }
  
  .loading-tip {
    font-size: 0.9rem;
  }
}
</style>